import { X, Trash2, ShoppingBag, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const CartSidebar = () => {
  const { items, removeFromCart, updateQuantity, isCartOpen, setIsCartOpen, totalPrice, totalItems } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Parse group_size to get max quantity
  const getMaxQuantity = (groupSize: string): number => {
    const match = groupSize.match(/(\d+)/g);
    if (match && match.length > 0) {
      const max = parseInt(match[match.length - 1], 10);
      return max || 20;
    }
    return 20;
  };

  const handleIncreaseQuantity = (itemId: string, currentQty: number, groupSize: string) => {
    const maxQty = getMaxQuantity(groupSize);
    if (currentQty >= maxQty) {
      toast.info(`Maximum ${maxQty} travelers allowed for this trip`);
      return;
    }
    updateQuantity(itemId, currentQty + 1);
    toast.success("Quantity updated");
  };

  const handleDecreaseQuantity = (itemId: string, currentQty: number) => {
    if (currentQty <= 1) {
      removeFromCart(itemId);
      toast.success("Item removed from cart");
    } else {
      updateQuantity(itemId, currentQty - 1);
      toast.success("Quantity updated");
    }
  };

  if (!isCartOpen) return null;

  const handleCheckout = () => {
    if (!user) {
      toast.info("Please sign in to proceed to checkout");
      setIsCartOpen(false);
      navigate("/auth", { state: { returnTo: "/checkout" } });
      return;
    }
    setIsCartOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-background shadow-elevated z-50 animate-slide-in-right">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-6 h-6 text-primary" />
              <h2 className="font-display text-xl font-semibold text-foreground">
                Your Cart ({totalItems})
              </h2>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsCartOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">Your cart is empty</p>
                <p className="text-sm text-muted-foreground mb-6">
                  Add some trips to start your adventure!
                </p>
                <Button onClick={() => setIsCartOpen(false)}>Browse Trips</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 rounded-lg bg-muted/50 animate-fade-in"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground truncate">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.destination}</p>
                      <p className="text-sm text-muted-foreground">{item.duration}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Quantity:</span>
                          <div className="flex items-center gap-1 border border-border rounded-md">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-muted"
                              onClick={() => handleDecreaseQuantity(item.id, item.quantity)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-medium text-foreground">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-muted"
                              onClick={() => handleIncreaseQuantity(item.id, item.quantity, item.groupSize)}
                              disabled={item.quantity >= getMaxQuantity(item.groupSize)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                        <span className="font-semibold text-primary">
                          ₹{item.price.toLocaleString('en-IN')} x {item.quantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => {
                              removeFromCart(item.id);
                              toast.success("Item removed from cart");
                            }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="p-6 border-t border-border bg-background">
              <div className="flex items-center justify-between mb-4">
                <span className="text-muted-foreground">Total</span>
                <span className="text-2xl font-bold text-foreground">₹{totalPrice.toLocaleString('en-IN')}</span>
              </div>
              <Button className="w-full" size="lg" onClick={handleCheckout}>
                Proceed to Checkout
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;
